import { useEffect, useMemo, useState } from "react";
import { BadgeIndianRupee, Banknote, ChevronDown, CircleSlash, ClipboardPenLine, History, Minus, Percent, Printer, ReceiptText, Search, ShoppingBag, Trash2, Truck, UtensilsCrossed, WalletCards, Plus, Minus as MinusIcon } from "lucide-react";
import { toast } from "sonner";
import { ALL_CATEGORIES, filterMenuItems, menuCategories } from "@/lib/menu";
import { MIN_QTY, fmtQty, lineTotal, roundQty } from "@/lib/qty";
import * as api from "@/lib/orderApi";
import { usePOS } from "@/context/POSContext";
import { CancelOrderDialog, Modal, NumberPad, VariantModal } from "@/components/Modals";
import CrmQuickPanel from "@/components/billing/CrmQuickPanel";
import ReportsPanel from "@/pages/ReportsPanelV2";
import BillHistoryPanel from "@/components/billing/BillHistoryPanel";
import TablePicker from "@/components/billing/TablePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useActiveOrders } from "@/hooks/useActiveOrders";
import { tableTone, toneLabel } from "@/lib/occupancy";
import { MONEY_CAPTION, MONEY_GRAND_TOTAL, MONEY_LINE, inr } from "@/lib/format";
import { EmptyState, LoadingState } from "@/components/ui/StateBoundaries";

const BILL_HISTORY = "bill_history";
const workspaceTabs = [["billing","Order desk",ReceiptText],["crm","CRM",WalletCards],["reports","Reports",BadgeIndianRupee]];

const mapServerLine = line => ({
  id: line.id,
  menuItemId: line.menu_item_id,
  name: line.item_name,
  price: Number(line.unit_price),
  qty: roundQty(line.quantity),
  variant: line.variation_name || null,
  modifier: (line.modifier_names || []).join(", ") || null,
  status: line.status,
});

/**
 * The `money` helper was `String(value)` behind a bare `₹`, at fourteen call sites.
 *
 * It applied no grouping, no fixed decimals and none of the configured currency
 * symbol — so the till printed ₹1240.5 in the totals column while the same figure
 * on the receipt preview beside it read ₹1,240.50, and a cashier comparing the two
 * had to do it character by character. Money renders through inr() now.
 *
 * What survives is `plain()`, and only for the things that are NOT money: the
 * service-charge PERCENTAGE. Putting 10 through inr() would print "₹10.00" beside
 * the word "Service charge" and invent a charge out of a rate.
 */
const plain = value => (value === undefined || value === null ? "0" : String(value));

function WorkspaceTabs({ active, onChange }) {
  return (
    <div className="flex items-center gap-1 bg-card border-b px-2 py-1 sticky top-0 z-30 shadow-sm" data-testid="reception-workspace-tabs">
      {workspaceTabs.map(([id,label,Icon]) => (
        <button
          key={id}
          className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-md text-body font-medium transition-colors ${active === id ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted"}`}
          onClick={() => onChange(id)}
          data-testid={`workspace-${id}-button`}
        >
          <Icon className="w-4 h-4"/>
          <span className="hidden sm:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}

function OrderModeSidebar({ active, onChange, orderTypes }) {
  const getIcon = name => {
    if (name === "dine_in") return UtensilsCrossed;
    if (name === "delivery") return Truck;
    if (name === "takeaway") return ShoppingBag;
    return ShoppingBag;
  };
  return (
    <aside className="w-16 md:w-24 bg-card border-r flex flex-col items-center py-4 gap-2" data-testid="order-mode-sidebar">
      {(orderTypes?.length ? orderTypes : [{name: 'dine_in', display_label: 'Dine-In'}, {name: 'delivery', display_label: 'Delivery'}, {name: 'takeaway', display_label: 'Takeaway'}]).map(({ name, display_label }) => {
        const Icon = getIcon(name);
        return (
          <button
            key={name}
            className={`w-12 h-12 md:w-20 md:h-16 flex flex-col items-center justify-center gap-1 rounded-xl transition-all ${active === name ? "bg-primary text-primary-foreground shadow-md ring-2 ring-primary ring-offset-1 ring-offset-background" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
            onClick={() => onChange(name)}
            data-testid={`order-mode-${name}-button`}
          >
            <Icon className="w-5 h-5"/>
            <span className="text-micro font-bold text-center">{display_label || name}</span>
          </button>
        );
      })}
      <button
        className={`w-12 h-12 md:w-20 md:h-16 flex flex-col items-center justify-center gap-1 rounded-xl transition-all mt-auto ${active === BILL_HISTORY ? "bg-primary text-primary-foreground shadow-md ring-2 ring-primary ring-offset-1 ring-offset-background" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
        onClick={() => onChange(BILL_HISTORY)}
        data-testid="order-mode-history-button"
      >
        <History className="w-5 h-5"/>
        <span className="text-micro font-bold text-center">History</span>
      </button>
    </aside>
  );
}

function MenuPanel({ category, search, items, categories, menuHydrated = true, menuEmpty = false, onCategory, onSearch, onItem }) {
  return (
    <section className="flex-1 flex flex-col bg-background min-w-0" data-testid="menu-panel">
      <div className="p-3 border-b bg-card flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => onSearch(e.target.value)}
            placeholder="Search item or short-code…"
            className="pl-9 h-10 bg-muted/50 border-muted"
            data-testid="menu-search-input"
          />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto p-2 bg-muted/30 border-b scrollbar-hide" data-testid="menu-categories">
        {categories.map(name => (
          <button
            key={name}
            className={`flex-none px-4 py-1.5 rounded-full text-meta font-semibold whitespace-nowrap transition-colors ${category === name ? "bg-primary text-primary-foreground shadow-sm" : "bg-card text-muted-foreground border hover:bg-muted"}`}
            onClick={() => onCategory(name)}
            data-testid={`category-${name.toLowerCase().replaceAll(" ","-")}-button`}
          >
            {name}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 content-start" data-testid="menu-item-grid">
        {items.map(item => (
          <button
            key={item.id}
            className="flex flex-col text-left p-3 rounded-xl border bg-card shadow-sm hover:shadow-md hover:border-primary/50 transition-all active:scale-[0.98] relative overflow-hidden"
            onClick={() => onItem(item)}
            data-testid={`menu-item-${item.id}-button`}
          >
            <span className={`w-3 h-3 border-[1.5px] rounded-sm flex items-center justify-center mb-2 ${item.veg ? "border-success/40" : "border-danger/40"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${item.veg ? "bg-success" : "bg-danger"}`}></span>
            </span>

            <small className="text-micro font-bold text-muted-foreground mb-hair truncate w-full">{item.code} · {item.category}</small>
            <strong className="text-body font-semibold text-card-foreground line-clamp-2 mb-snug flex-1">{item.name}</strong>

            <div className="flex justify-between items-end w-full mt-auto gap-hair">
              <b className={`${MONEY_LINE} text-primary`}>{inr(item.price)}</b>
              {item.variants && <span className="text-micro font-bold bg-primary/10 text-primary px-hair rounded-sm">Varies</span>}
            </div>
          </button>
        ))}
        {/* Three states, not one. "Try a different search term" was shown for a menu
            that had never loaded from this server, which sent cashiers hunting for a
            spelling instead of a sync. `menuHydrated` separates still-reading from
            read-and-empty; a non-empty menu that filters to nothing is the only case
            the search advice is true for. */}
        {!items.length && (
          <div className="col-span-full py-12 flex justify-center">
            {!menuHydrated
              ? <LoadingState message="Reading the menu…" testId="menu-loading" />
              : menuEmpty
                ? <EmptyState title="Menu not loaded" message="No menu has been loaded from this server yet. Check the connection and reload — do not bill from an empty menu." icon={UtensilsCrossed} testId="menu-not-loaded" />
                : <EmptyState title="No items found" message="Try a different search term or category." icon={UtensilsCrossed} testId="menu-no-match" />}
          </div>
        )}
      </div>
    </section>
  );
}

function CartItem({ item, index, onQuantity, onDelete }) {
  const isUnsent = item.status === "pending";
  return (
    <div className={`flex items-start gap-3 p-3 border-b transition-colors ${isUnsent ? 'bg-warning/50 hover:bg-warning/50' : 'bg-card hover:bg-muted/30'}`} data-testid={`cart-item-${index}`}>
      <button
        className="w-8 h-8 rounded-md bg-primary/10 text-primary font-bold flex items-center justify-center border border-primary/20 hover:bg-primary hover:text-primary-foreground transition-colors shrink-0"
        onClick={() => onQuantity(item)}
        data-testid={`cart-item-${index}-quantity-button`}
      >
        {fmtQty(item.qty)}
      </button>

      <div className="flex-1 flex flex-col min-w-0">
        <strong className="text-body font-semibold text-card-foreground truncate">{item.name}</strong>
        <div className="flex items-center gap-tight mt-hair">
          {isUnsent && <span className="text-micro font-bold bg-warning-subtle text-warning px-hair rounded-sm">Unsent</span>}
          {(item.variant || item.modifier) && <span className="text-micro font-medium text-danger bg-danger-subtle px-hair rounded-sm truncate" data-testid={`cart-item-${index}-modifier`}>{item.variant || item.modifier}</span>}
          <span className={`${MONEY_CAPTION} text-muted-foreground`}>{inr(item.price)} each</span>
        </div>
      </div>

      <div className="flex flex-col items-end gap-hair shrink-0">
        <b className={MONEY_LINE} data-testid={`cart-item-${index}-line-total`}>{inr(lineTotal(item.price, item.qty))}</b>
        <button
          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 p-1 rounded-md transition-colors"
          onClick={() => onDelete(item)}
          data-testid={`cart-item-${index}-delete-button`}
          aria-label={`Remove ${item.name}`}
        >
          <Trash2 className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}

const CANCELLABLE = new Set(["open", "kot_sent"]);

function CartPanel({ mode, order, tableName, captain, cart, preview, error, busy, onPickTable, onDiscount, onNote, onKot, onHold, onPay, onQuantity, onDelete, onCancelOrder }) {
  const total = inr(preview?.total);
  const cancellable = !!order && CANCELLABLE.has(order.status);

  return (
    <aside className="w-full md:w-80 lg:w-96 flex flex-col bg-card border-l border-t md:border-t-0 shadow-lg shrink-0 h-[50vh] md:h-auto" data-testid="active-cart-panel">
      {/* Casing through CSS, not through the string: "CAPTAIN" and "… ORDER" were
          typed in capitals, which a screen reader spells out and a translation cannot
          lower-case. The words are ordinary now and `uppercase` does the shouting. */}
      <header className="px-base py-snug border-b flex justify-between gap-snug bg-muted/20">
        <div className="flex flex-col items-start min-w-0">
          <small className="text-micro font-bold text-primary">{mode?.replace("_", " ")} order</small>
          <button
            className="flex items-center gap-hair font-bold text-lede hover:text-primary transition-colors"
            onClick={onPickTable}
            data-testid="table-selector-button"
          >
            {tableName || "Pick table"}
            <ChevronDown className="w-4 h-4 text-muted-foreground"/>
          </button>
        </div>
        <div className="flex flex-col items-end text-right min-w-0">
          <small className="text-micro font-bold text-muted-foreground">Captain</small>
          <strong className="text-body font-semibold truncate">{captain}</strong>
        </div>
      </header>

      <div className="flex justify-between items-center gap-snug px-base py-hair bg-muted/40 text-micro font-bold text-muted-foreground border-b">
        <span data-testid="cart-order-number">{order ? `Order #${order.orderNumber}` : "No open order"}</span>
        <span data-testid="cart-cover-count">{order ? `${order.guestCount} covers` : "—"}</span>
      </div>

      {error && <div className="bg-destructive/10 text-destructive text-meta font-semibold p-snug border-b border-destructive/20" data-testid="order-error-message">{error}</div>}

      <div className="flex-1 overflow-y-auto bg-background" data-testid="cart-item-list">
        {cart.length ? (
          cart.map((item,index) => <CartItem item={item} index={index} onQuantity={onQuantity} onDelete={onDelete} key={`${item.id}-${item.variant || index}`}/>)
        ) : (
          <div className="h-full flex items-center justify-center" data-testid="empty-cart-message">
            <EmptyState icon={ShoppingBag} title="Empty Order" message="Tap menu items to start an order" />
          </div>
        )}
      </div>

      <div className="px-base py-snug bg-muted/10 border-t space-y-hair" data-testid="cart-totals">
        <div className="flex justify-between gap-snug text-meta text-muted-foreground"><span className="">Subtotal</span><b className={`${MONEY_LINE} text-foreground`}>{inr(preview?.subtotal)}</b></div>
        {Number(preview?.discount_amount) > 0 && <div className="flex justify-between gap-snug text-meta text-success"><span className="">Discount</span><b className={MONEY_LINE}>− {inr(preview?.discount_amount)}</b></div>}
        <div className="flex justify-between gap-snug text-meta text-muted-foreground"><span className="">GST</span><b className={`${MONEY_LINE} text-foreground`}>{inr(preview?.tax)}</b></div>
        {Number(preview?.service_charge) > 0 && <div className="flex justify-between gap-snug text-meta text-muted-foreground"><span className="">Service charge ({plain(preview?.service_charge_pct)}%)</span><b className={`${MONEY_LINE} text-foreground`}>{inr(preview?.service_charge)}</b></div>}
        {Number(preview?.round_off) !== 0 && preview?.round_off !== undefined && <div className="flex justify-between gap-snug text-meta text-muted-foreground"><span className="">Round off</span><b className={`${MONEY_LINE} text-foreground`}>{inr(preview?.round_off)}</b></div>}
        <div className="flex justify-between items-baseline gap-snug pt-snug mt-snug border-t border-dashed">
          <span className="text-meta font-bold text-muted-foreground">Amount due</span>
          <strong className={`${MONEY_GRAND_TOTAL} text-primary`} data-testid="cart-total-amount">{total}</strong>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-snug p-snug border-t bg-card">
        <Button variant="outline" size="sm" className="h-10 text-micro font-bold" onClick={onDiscount} disabled={!order} data-testid="apply-discount-button"><Percent className="w-3.5 h-3.5 mr-tight"/>Disc.</Button>
        <Button variant="outline" size="sm" className="h-10 text-micro font-bold" onClick={onNote} disabled={!order} data-testid="add-order-note-button"><ClipboardPenLine className="w-3.5 h-3.5 mr-tight"/>Note</Button>
        <Button variant="outline" size="sm" className="h-10 text-micro font-bold" onClick={onKot} disabled={!order || busy} data-testid="print-kot-button"><Printer className="w-3.5 h-3.5 mr-tight"/>KOT</Button>
        <Button variant="outline" size="sm" className="h-10 text-micro font-bold" onClick={onHold} disabled={!order} data-testid="hold-bill-button"><Minus className="w-3.5 h-3.5 mr-tight"/>Hold</Button>
        <Button variant="destructive" size="sm" className="h-10 text-micro font-bold" onClick={onCancelOrder} disabled={!cancellable || busy} title={order && !cancellable ? "The bill is printed — reopening it is an admin override" : "Void the whole order and free the table"} data-testid="cancel-order-button"><CircleSlash className="w-3.5 h-3.5 mr-tight"/>Void</Button>
        <Button variant="action" size="sm" className="h-10 text-meta font-bold col-span-3 tabular-nums" onClick={onPay} disabled={!order || !preview || busy} data-testid="settle-pay-button"><Banknote className="w-4 h-4 mr-snug"/>Settle · {total}</Button>
      </div>
    </aside>
  );
}

// TablePicker is now imported from @/components/billing/TablePicker

import { ReceiptPreview } from "@/components/billing/ReceiptPreview";

function PaymentDialog({ total, document, busy, onClose, onComplete, paymentModes }) {
  return (
    <Modal title="Settle bill" onClose={onClose} testId="payment-modal">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1">
          <div className={`text-center ${MONEY_GRAND_TOTAL} text-primary my-loose`} data-testid="payment-total">{total}</div>
          <div className="grid grid-cols-3 gap-snug">
            {(paymentModes?.length ? paymentModes : [{name: 'cash', display_label: 'Cash', is_cash: true}]).map((pm) => (
              <button
                key={pm.name}
                onClick={() => onComplete(pm.name)}
                disabled={busy}
                data-testid={`payment-${pm.name.toLowerCase()}-button`}
                className="flex flex-col items-center justify-center p-4 border-2 rounded-xl bg-card hover:border-primary hover:bg-primary/5 transition-colors group"
              >
                {pm.is_cash ? <Banknote className="w-6 h-6 text-muted-foreground group-hover:text-primary mb-snug"/> : <WalletCards className="w-6 h-6 text-muted-foreground group-hover:text-primary mb-snug"/>}
                <strong className="text-lede font-bold">{pm.display_label || pm.name}</strong>
                <small className="text-micro text-muted-foreground mt-hair text-center">{busy ? "Settling…" : "Receive full amount"}</small>
              </button>
            ))}
          </div>
        </div>

        {document && (
          <div className="hidden md:block w-[300px] shrink-0 border-l border-border pl-6 max-h-[60vh] overflow-y-auto scrollbar-hide">
             <ReceiptPreview document={document} />
          </div>
        )}
      </div>
    </Modal>
  );
}

function VoidDialog({ item, reasons, onConfirm, onClose }) {
  const [reasonId, setReasonId] = useState(reasons[0]?.id ?? "");
  const fired = item.status !== "pending";
  return (
    <Modal title={`Void ${item.name}`} onClose={onClose} testId="void-item-modal">
      <p className="text-body text-muted-foreground mb-6" data-testid="void-item-context">
        {fired
          ? "This line is already with the kitchen. Voiding prints a VOID slip at its station."
          : "This line has not been sent yet."}
      </p>
      {reasons.length > 0 && (
        <div className="mb-6 space-y-2">
          <label className="text-meta font-bold text-muted-foreground">Reason</label>
          <select
            value={reasonId}
            onChange={event => setReasonId(event.target.value)}
            data-testid="void-reason-select"
            className="w-full h-12 border rounded-md px-3 bg-card text-foreground"
          >
            {reasons.map(reason => <option value={reason.id} key={reason.id}>{reason.label}</option>)}
          </select>
        </div>
      )}
      <Button variant="destructive" className="w-full h-12 text-body font-bold" onClick={() => onConfirm(reasonId ? Number(reasonId) : null)} data-testid="confirm-void-button">
        Void this line
      </Button>
    </Modal>
  );
}

function NoteDialog({ initial, onSave, onClose }) {
  const [text, setText] = useState(initial || "");
  return (
    <Modal title="Order note" onClose={onClose} testId="order-note-modal">
      <textarea
        className="w-full min-h-[120px] p-4 border rounded-md bg-card resize-y focus:outline-none focus:ring-2 focus:ring-primary/50 mb-6"
        value={text}
        onChange={event => setText(event.target.value)}
        placeholder="Allergy, seating, kitchen instruction…"
        data-testid="order-note-input"
        rows={4}
      />
      <Button variant="action" className="w-full h-12 text-body font-bold" onClick={() => onSave(text.trim())} data-testid="save-order-note-button">
        Save note
      </Button>
    </Modal>
  );
}

function DiscountDialog({ current, onApply, onClose }) {
  const [kind, setKind] = useState(current.discountPct !== null ? "percent" : "amount");
  const [value, setValue] = useState(String(current.discountPct ?? current.discount ?? 0));
  const numeric = Number(value);
  const invalid = Number.isNaN(numeric) || numeric < 0 || (kind === "percent" && numeric > 100);
  return (
    <Modal title="Apply discount" onClose={onClose} testId="discount-modal">
      <div className="flex gap-2 mb-4">
        <Button variant={kind === "percent" ? "default" : "outline"} className="flex-1" onClick={() => setKind("percent")} data-testid="discount-kind-percent">Percent</Button>
        <Button variant={kind === "amount" ? "default" : "outline"} className="flex-1" onClick={() => setKind("amount")} data-testid="discount-kind-amount">Flat ₹</Button>
      </div>
      <div className="mb-6">
        <Input
          className="h-16 text-center text-display font-bold bg-muted/20"
          type="number"
          min="0"
          value={value}
          onChange={event => setValue(event.target.value)}
          data-testid="discount-value-input"
          autoFocus
        />
        {invalid && <p className="text-meta font-semibold text-destructive mt-2 text-center" data-testid="discount-error">Enter {kind === "percent" ? "a percentage between 0 and 100" : "a positive amount"}.</p>}
      </div>
      <div className="flex gap-3">
        <Button variant="outline" className="flex-1 h-12" onClick={() => onApply({ discount: 0, discountPct: null })} data-testid="clear-discount-button">Clear</Button>
        <Button variant="action" className="flex-1 h-12" disabled={invalid} onClick={() => onApply(kind === "percent" ? { discount: 0, discountPct: numeric } : { discount: numeric, discountPct: null })} data-testid="apply-discount-confirm-button">Apply</Button>
      </div>
    </Modal>
  );
}

export default function BillingPage() {
  const { menu, menuHydrated, tables, user, token, permissions, order, preview, orderError, orderBusy, openOrder, punchItem, fireOrderKot, setDiscount, discount, settleOrder, setOrderNote, holdOrder, cancelItems, cancelOrder, subscribeRealtime, paymentModes, orderTypes } = usePOS();
  const { orderForTable, refresh } = useActiveOrders(token, subscribeRealtime);
  const [workspace, setWorkspace] = useState("billing");
  const [mode, setMode] = useState("dine_in");
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [search, setSearch] = useState("");
  const [variantItem, setVariantItem] = useState(null);
  const [quantityItem, setQuantityItem] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [tablePickerOpen, setTablePickerOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [voidItem, setVoidItem] = useState(null);
  const [voidReasons, setVoidReasons] = useState([]);
  const [cancelPrompt, setCancelPrompt] = useState(null);

  const filteredItems = useMemo(() => filterMenuItems(menu, category, search), [menu, category, search]);
  const categoryTabs = useMemo(() => menuCategories(menu), [menu]);

  const cart = useMemo(
    () => (order?.items || []).filter(line => line.status !== "cancelled").map(mapServerLine),
    [order]
  );
  const tableName = useMemo(() => tables.find(t => t.id === order?.tableId)?.name || null, [tables, order]);

  const addMenuItem = item => {
    if (!order) { setTablePickerOpen(true); return; }
    if (item.variants) { setVariantItem(item); return; }
    punchItem(item);
  };

  const pickTable = async table => {
    const orderType = mode;
    if (orderType === BILL_HISTORY) { toast.error("Bill History does not open an order"); return; }
    setTablePickerOpen(false);
    await openOrder(table, orderType);
  };

  const fireKot = async () => {
    if (!cart.length) { toast.error("Add an item before printing KOT"); return; }
    const result = await fireOrderKot();
    if (result) toast.success(`KOT sent · ${result.jobs?.length ?? 0} print job(s)`);
  };

  useEffect(() => {
    if (!voidItem || voidReasons.length || !token) return;
    api
      .listCancellationReasons(token)
      .then(rows => setVoidReasons(rows.filter(r => r.applies_to !== "order")))
      .catch(() => setVoidReasons([]));
  }, [token, voidItem, voidReasons.length]);

  const confirmVoid = async reasonId => {
    const voided = voidItem;
    const result = await cancelItems([voidItem.id], reasonId);
    setVoidItem(null);
    if (!result) return;
    if (result.orderCancelled) {
      toast.success(`${voided.name} voided — nothing left on order #${result.orderNumber}, so it is closed${result.tableFreed ? " and the table is free" : ""}`);
      return;
    }
    toast.success(`${voided.name} voided`);
    const stillLive = (result.items || []).filter(line => line.status !== "cancelled");
    if (!stillLive.length) setCancelPrompt("last-line");
  };

  const confirmCancelOrder = async ({ reasonCode, reason, authorizedBy }) => {
    const cancelled = order;
    const result = await cancelOrder({ reasonCode, reason, authorizedBy });
    if (!result) return;
    setCancelPrompt(null);
    toast.success(
      `Order #${cancelled.orderNumber} cancelled` +
        (result.void_slips ? ` · ${result.void_slips} VOID slip(s) sent` : "") +
        (result.table_freed ? " · table freed" : "")
    );
  };

  const saveNote = async text => {
    const result = await setOrderNote(text);
    if (result) {
      toast.success("Note saved to the order");
      setNoteOpen(false);
    }
  };

  const hold = () => {
    const held = holdOrder();
    if (held) toast.success(`Order #${held.orderNumber} held — reopen it from the table`);
  };

  const completePayment = async method => {
    const result = await settleOrder(method);
    if (result) {
      toast.success(`${method} settled · ${inr(result.total)}`);
      setPaymentOpen(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background" data-testid="billing-workspace">
      <WorkspaceTabs active={workspace} onChange={setWorkspace}/>

      {workspace === "crm" ? (
        <CrmQuickPanel/>
      ) : workspace === "reports" ? (
        <ReportsPanel/>
      ) : mode === BILL_HISTORY ? (
        <div className="flex flex-1 overflow-hidden">
          <OrderModeSidebar active={mode} onChange={setMode} orderTypes={orderTypes}/>
          <div className="flex-1 overflow-y-auto bg-background p-4"><BillHistoryPanel token={token} permissions={permissions} role={user?.role}/></div>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          <div className="hidden md:flex"><OrderModeSidebar active={mode} onChange={setMode} orderTypes={orderTypes}/></div>
          <MenuPanel category={category} search={search} items={filteredItems} categories={categoryTabs} menuHydrated={menuHydrated} menuEmpty={!menu?.length} onCategory={setCategory} onSearch={setSearch} onItem={addMenuItem}/>
          <CartPanel mode={mode} order={order} tableName={tableName} captain={user.name.split(" ")[0]} cart={cart} preview={preview} error={orderError} busy={orderBusy} onPickTable={() => setTablePickerOpen(true)} onDiscount={() => setDiscountOpen(true)} onNote={() => setNoteOpen(true)} onKot={fireKot} onHold={hold} onPay={() => setPaymentOpen(true)} onQuantity={setQuantityItem} onDelete={setVoidItem} onCancelOrder={() => setCancelPrompt("manual")}/>
        </div>
      )}

      {tablePickerOpen && <TablePicker tables={tables} orderForTable={orderForTable} busy={orderBusy} onPick={pickTable} onClose={() => setTablePickerOpen(false)} refreshOrders={refresh} />}
      {variantItem && <VariantModal item={variantItem} onSelect={variant => { punchItem(variantItem, variant); setVariantItem(null); }} onClose={() => setVariantItem(null)}/>}

      {quantityItem && (
        <NumberPad
          item={quantityItem}
          onSave={qty => {
            const extra = roundQty(qty - quantityItem.qty);
            if (extra >= MIN_QTY) punchItem({ id: String(quantityItem.menuItemId) }, null, extra);
            else if (extra > 0) { toast.error(`Smallest step is ${fmtQty(MIN_QTY)}`); return; }
            else if (extra < 0) { setVoidItem(quantityItem); setQuantityItem(null); return; }
            setQuantityItem(null);
          }}
          onClose={() => setQuantityItem(null)}
        />
      )}

      {voidItem && <VoidDialog item={voidItem} reasons={voidReasons} onConfirm={confirmVoid} onClose={() => setVoidItem(null)}/>}
      {noteOpen && order && <NoteDialog initial={order.notes} onSave={saveNote} onClose={() => setNoteOpen(false)}/>}
      {discountOpen && <DiscountDialog current={discount} onApply={next => { setDiscount(next); setDiscountOpen(false); }} onClose={() => setDiscountOpen(false)}/>}
      {paymentOpen && preview && <PaymentDialog total={inr(preview.total)} document={preview.document} busy={orderBusy} onClose={() => setPaymentOpen(false)} onComplete={completePayment} paymentModes={paymentModes}/>}
      {cancelPrompt && order && <CancelOrderDialog order={order} token={token} busy={orderBusy} lastLine={cancelPrompt === "last-line"} onConfirm={confirmCancelOrder} onClose={() => setCancelPrompt(null)}/>}
    </div>
  );
}